import { useState } from "react";
import {
  Cross, Flower2, Moon, Heart, Landmark, HelpCircle, ScrollText, Infinity,
  Award, HandMetal, Bird, Bell, Atom, Palette, FlaskConical,
  Star, Lightbulb, Feather, BookOpen, Drama, Swords, Crown,
  X, Sparkles, Search, Check, Users
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

const ICON_MAP = {
  cross: Cross, flower2: Flower2, moon: Moon, heart: Heart,
  landmark: Landmark, "help-circle": HelpCircle, scroll: ScrollText, infinity: Infinity,
  award: Award, "hand-fist": HandMetal, dove: Bird, bell: Bell,
  atom: Atom, palette: Palette, "flask-conical": FlaskConical,
  star: Star, lightbulb: Lightbulb, feather: Feather,
  "book-open": BookOpen, drama: Drama, swords: Swords, crown: Crown,
};

const CATEGORY_COLORS = {
  spiritual: "from-violet-500/10 to-purple-500/10 border-violet-300/40",
  philosophy: "from-indigo-500/10 to-blue-500/10 border-indigo-300/40",
  leadership: "from-red-500/10 to-orange-500/10 border-red-300/40",
  science: "from-emerald-500/10 to-teal-500/10 border-emerald-300/40",
  modern: "from-amber-500/10 to-yellow-500/10 border-amber-300/40",
  african: "from-pink-500/10 to-rose-500/10 border-pink-300/40",
  strategy: "from-orange-500/10 to-red-500/10 border-orange-300/40",
};

const CATEGORY_LABELS = {
  spiritual: "Spiritual & Religious",
  philosophy: "Philosophy",
  leadership: "Leadership",
  science: "Science & Innovation",
  modern: "Modern Influence",
  african: "African Wisdom",
  strategy: "Strategy & Power",
};

export function WisdomCouncil({ figures, onSelect, onClose, multiSelect = false, onMultiSelect }) {
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selected, setSelected] = useState(new Set());

  const categories = [...new Set(figures.map(f => f.category))];

  const filtered = figures.filter(f => {
    const matchesSearch = !search || f.name.toLowerCase().includes(search.toLowerCase()) ||
      f.philosophy.toLowerCase().includes(search.toLowerCase()) ||
      f.tradition.toLowerCase().includes(search.toLowerCase());
    const matchesCat = !selectedCategory || f.category === selectedCategory;
    return matchesSearch && matchesCat;
  });

  const grouped = {};
  filtered.forEach(f => {
    if (!grouped[f.category]) grouped[f.category] = [];
    grouped[f.category].push(f);
  });

  const toggleFigure = (fig) => {
    if (multiSelect) {
      setSelected(prev => {
        const next = new Set(prev);
        if (next.has(fig.key)) {
          next.delete(fig.key);
        } else if (next.size < 7) {
          next.add(fig.key);
        }
        return next;
      });
    } else {
      onSelect(fig);
    }
  };

  const confirmMultiSelect = () => {
    const selectedFigures = figures.filter(f => selected.has(f.key));
    onMultiSelect?.(selectedFigures);
  };

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="flex items-center justify-between p-3 sm:p-4 border-b border-border/50">
        <div className="flex items-center gap-2">
          <div className={cn(
            "w-8 h-8 rounded-full flex items-center justify-center",
            multiSelect
              ? "bg-gradient-to-br from-violet-400 to-purple-500"
              : "bg-gradient-to-br from-amber-400 to-orange-500"
          )}>
            {multiSelect ? <Users className="w-4 h-4 text-white" /> : <Sparkles className="w-4 h-4 text-white" />}
          </div>
          <div>
            <h2 data-testid="wisdom-council-title" className="text-base sm:text-lg font-heading font-semibold">
              {multiSelect ? "Council Roundtable" : "Wisdom Council"}
            </h2>
            <p className="text-[10px] sm:text-xs text-muted-foreground">
              {multiSelect
                ? `Select up to 7 figures (${selected.size} chosen)`
                : `${figures.length} historical minds to consult`}
            </p>
          </div>
        </div>
        <button data-testid="wisdom-close-btn" onClick={onClose} className="p-2 rounded-xl hover:bg-muted/50 transition-colors">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Search */}
      <div className="px-3 sm:px-4 pt-3">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            data-testid="wisdom-search"
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search figures, traditions..."
            className="w-full pl-9 pr-3 py-2 rounded-xl border border-border/50 bg-muted/20 text-sm focus:outline-none focus:border-primary/40"
          />
        </div>
      </div>

      {/* Category Filter */}
      <div className="flex items-center gap-1.5 px-3 sm:px-4 py-2 overflow-x-auto">
        <button
          data-testid="wisdom-cat-all"
          onClick={() => setSelectedCategory(null)}
          className={cn(
            "px-2.5 py-1 rounded-full text-[11px] font-medium whitespace-nowrap transition-all border",
            !selectedCategory ? "bg-foreground text-background border-foreground" : "border-border/50 text-muted-foreground hover:bg-muted/50"
          )}
        >
          All
        </button>
        {categories.map(cat => (
          <button
            key={cat}
            data-testid={`wisdom-cat-${cat}`}
            onClick={() => setSelectedCategory(selectedCategory === cat ? null : cat)}
            className={cn(
              "px-2.5 py-1 rounded-full text-[11px] font-medium whitespace-nowrap transition-all border",
              selectedCategory === cat ? "bg-foreground text-background border-foreground" : "border-border/50 text-muted-foreground hover:bg-muted/50"
            )}
          >
            {CATEGORY_LABELS[cat] || cat}
          </button>
        ))}
      </div>

      {/* Figures Grid */}
      <div className="flex-1 overflow-y-auto px-3 sm:px-4 pb-4 space-y-4">
        {Object.entries(grouped).map(([cat, figs]) => (
          <div key={cat}>
            <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">
              {CATEGORY_LABELS[cat] || cat}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              {figs.map(fig => {
                const FigIcon = ICON_MAP[fig.avatar] || Star;
                const isSelected = multiSelect && selected.has(fig.key);
                return (
                  <button
                    key={fig.key}
                    data-testid={`wisdom-figure-${fig.key}`}
                    onClick={() => toggleFigure(fig)}
                    className={cn(
                      "relative flex flex-col items-center gap-1.5 p-3 rounded-2xl border bg-gradient-to-br transition-all hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] text-center group",
                      isSelected
                        ? "border-primary ring-2 ring-primary/30 shadow-md"
                        : CATEGORY_COLORS[cat] || "border-border/50"
                    )}
                  >
                    {multiSelect && isSelected && (
                      <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                        <Check className="w-3 h-3 text-primary-foreground" strokeWidth={3} />
                      </div>
                    )}
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-background/80 flex items-center justify-center shadow-sm group-hover:shadow-md transition-shadow">
                      <FigIcon className="w-5 h-5 sm:w-6 sm:h-6 text-foreground/70" strokeWidth={1.5} />
                    </div>
                    <span className="text-xs sm:text-sm font-medium leading-tight">{fig.name}</span>
                    <span className="text-[9px] sm:text-[10px] text-muted-foreground leading-tight line-clamp-2">{fig.era} &middot; {fig.tradition}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            <p className="text-sm">No figures match your search</p>
          </div>
        )}
      </div>

      {/* Multi-select confirm bar */}
      {multiSelect && selected.size > 0 && (
        <div className="border-t border-border/50 p-3 sm:p-4 bg-card/80 backdrop-blur-sm">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0 overflow-x-auto">
              {[...selected].map(key => {
                const fig = figures.find(f => f.key === key);
                if (!fig) return null;
                const FigIcon = ICON_MAP[fig.avatar] || Star;
                return (
                  <div key={key} className="flex items-center gap-1 px-2 py-1 rounded-full bg-primary/10 text-xs font-medium text-primary whitespace-nowrap flex-shrink-0">
                    <FigIcon className="w-3 h-3" strokeWidth={2} />
                    {fig.name.split(" ").pop()}
                  </div>
                );
              })}
            </div>
            <Button
              data-testid="roundtable-confirm-btn"
              onClick={confirmMultiSelect}
              className="btn-omni bg-primary text-primary-foreground gap-1.5 text-sm flex-shrink-0"
            >
              <Users className="w-4 h-4" />
              Consult {selected.size}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export function WisdomFigureBadge({ figure, onClear }) {
  if (!figure) return null;
  const FigIcon = ICON_MAP[figure.avatar] || Star;
  return (
    <div data-testid="wisdom-active-badge" className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 dark:bg-amber-950/30 border border-amber-200/50 dark:border-amber-800/30 text-xs">
      <FigIcon className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" strokeWidth={1.5} />
      <span className="font-medium text-amber-800 dark:text-amber-300">
        Consulting {figure.name}
      </span>
      <button data-testid="wisdom-clear-btn" onClick={onClear} className="ml-0.5 p-0.5 rounded-full hover:bg-amber-200/50 dark:hover:bg-amber-800/30 transition-colors">
        <X className="w-3 h-3 text-amber-600 dark:text-amber-400" />
      </button>
    </div>
  );
}

export function RoundtableBadge({ figures, onClear }) {
  if (!figures || figures.length === 0) return null;
  return (
    <div data-testid="roundtable-active-badge" className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-violet-50 dark:bg-violet-950/30 border border-violet-200/50 dark:border-violet-800/30 text-xs">
      <Users className="w-3.5 h-3.5 text-violet-600 dark:text-violet-400" strokeWidth={1.5} />
      <span className="font-medium text-violet-800 dark:text-violet-300">
        Council: {figures.map(f => f.name.split(" ").pop()).join(", ")}
      </span>
      <button data-testid="roundtable-clear-btn" onClick={onClear} className="ml-0.5 p-0.5 rounded-full hover:bg-violet-200/50 dark:hover:bg-violet-800/30 transition-colors">
        <X className="w-3 h-3 text-violet-600 dark:text-violet-400" />
      </button>
    </div>
  );
}
